import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaPhone,
  FaHeadphones,
  FaSignal,
  FaBellSlash,
  FaClosedCaptioning,
  FaLock,
} from 'react-icons/fa';
import { useEffect } from 'react';
import { useVoiceCall } from '@/contexts/VoiceCallContext';

export default function VoiceCallOverlay() {
  const {
    isInCall,
    channelName,
    isMuted,
    isDeafened,
    users,
    connectionQuality,
    toggleMute,
    toggleDeafen,
    leaveVoiceChannel,
    isDnd,
    captionsEnabled,
    toggleCaptions,
    toggleDnd,
  } = useVoiceCall();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'c' || e.key === 'C') {
        toggleCaptions();
      }
    };

    if (isInCall) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isInCall, toggleCaptions]);

  if (!isInCall) {
    return null;
  }

  const getConnectionColor = () => {
    switch (connectionQuality) {
      case 'good':
        return 'text-green-500';
      case 'medium':
        return 'text-yellow-500';
      case 'poor':
        return 'text-brand-error';
      default:
        return 'text-brand-outline';
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-green-600 to-green-700 text-brand-on-primary shadow-lg border-t border-green-800">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Voice channel info */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <FaSignal className={`${getConnectionColor()} hidden sm:block`} size={14} />
              <span className="font-semibold text-xs sm:text-sm truncate">🔊 {channelName}</span>
            </div>
            <div
              className="inline-flex items-center gap-1 text-[11px] bg-green-900/40 border border-green-400/40 text-green-200 px-2 py-0.5 rounded-full font-medium shadow-sm"
              title="Voice audio is encrypted end-to-end (WebRTC DTLS-SRTP)"
            >
              <FaLock size={9} className="text-green-300" />
              <span className="hidden sm:inline">Encrypted</span>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-xs bg-green-800/30 px-2 py-1 rounded">
              <span>{users.length > 0 ? users.length : 1}</span>
              <span>participant{users.length > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Voice controls */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Mute button */}
            <button
              onClick={toggleMute}
              className={`p-2.5 sm:p-3 rounded-full transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-white focus:outline-none ${
                isMuted
                  ? 'bg-brand-error hover:bg-brand-error'
                  : 'bg-green-800/40 hover:bg-green-800/60'
              }`}
              title={isMuted ? 'Unmute' : 'Mute'}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={isMuted}
            >
              {isMuted ? (
                <FaMicrophoneSlash className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <FaMicrophone className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>

            {/* Deafen button - hidden on small screens */}
            <button
              onClick={toggleDeafen}
              className={`hidden sm:block p-2.5 sm:p-3 rounded-full transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-white focus:outline-none ${
                isDeafened
                  ? 'bg-brand-error hover:bg-brand-error'
                  : 'bg-green-800/40 hover:bg-green-800/60'
              }`}
              title={isDeafened ? 'Undeafen' : 'Deafen'}
              aria-label={isDeafened ? 'Undeafen audio' : 'Deafen audio'}
              aria-pressed={isDeafened}
            >
              <FaHeadphones className={`w-4 h-4 sm:w-5 sm:h-5 ${isDeafened ? 'opacity-50' : ''}`} />
            </button>

            {/* Captions toggle */}
            <button
              onClick={toggleCaptions}
              className={`hidden sm:block p-2.5 sm:p-3 rounded-full transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-white focus:outline-none ${
                captionsEnabled
                  ? 'bg-brand-primary hover:bg-brand-primary-dark'
                  : 'bg-green-800/40 hover:bg-green-800/60'
              }`}
              title={captionsEnabled ? 'Disable Live Captions' : 'Enable Live Captions'}
              aria-label={captionsEnabled ? 'Disable Live Captions' : 'Enable Live Captions'}
              aria-pressed={captionsEnabled}
            >
              <FaClosedCaptioning
                className={`w-4 h-4 sm:w-5 sm:h-5 ${captionsEnabled ? 'opacity-100' : 'opacity-50'}`}
              />
            </button>

            {/* DND toggle */}
            <button
              onClick={() => toggleDnd()}
              className={`hidden sm:block p-2.5 sm:p-3 rounded-full transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-white focus:outline-none ${
                isDnd
                  ? 'bg-brand-surface hover:bg-brand-surface-variant'
                  : 'bg-green-800/40 hover:bg-green-800/60'
              }`}
              title={isDnd ? 'Disable Do Not Disturb' : 'Enable Do Not Disturb'}
              aria-label={isDnd ? 'Disable Do Not Disturb' : 'Enable Do Not Disturb'}
              aria-pressed={isDnd}
            >
              <FaBellSlash className={`w-4 h-4 sm:w-5 sm:h-5 ${isDnd ? 'opacity-80' : ''}`} />
            </button>

            {/* Leave button */}
            <button
              onClick={leaveVoiceChannel}
              className="p-2.5 sm:p-3 rounded-full bg-brand-error hover:bg-brand-error transition-all touch-manipulation focus-visible:ring-2 focus-visible:ring-white focus:outline-none"
              title="Leave voice channel"
              aria-label="Leave voice channel"
            >
              <FaPhone className="w-4 h-4 sm:w-5 sm:h-5 rotate-[135deg]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
